import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';
import { skipToken } from '@reduxjs/toolkit/query';

import { captureError } from '@clatter/platform';
import { buttonVariants } from '@clatter/ui';
import { NgBuildIcon, NgButton, NgCard, NgCardContent, NgCardHeader, SkeletonLoader } from '../../UI';
import { RootState, addNotification, useAppDispatch } from '../../../store';
import { getLongPullUIState } from '../../../store/UI/longPull.slice';
import { useUpload } from '../../../hooks/useUpload';
import { foldersService, useGetFolderPathQuery } from '../../../services/folders.service';
import { useCancelUploadRequestMutation } from '../../../services/slides.service';
import { useUploadNewAsset } from '../UploadNewAssetContext';
import { SlideItem, getSlideCompatibilityBadge } from './SlideItem';

const SKELETON_SLIDE_COUNT = 6;

export interface UploadedSlide {
  slide_number: number;
  thumbnail_base64?: string;
  thumbnail_location?: string;
  slide_title?: string;
  layout_name?: string | null;
  layout_in_all_themes?: boolean;
  matched_theme_names?: string[];
  not_matched_theme_names?: string[];
}

const StyledHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledTitle = styled.h2`
  margin: 0;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSize.h4};
  color: ${({ theme }) => theme.palette.text};
`;

const StyledSubtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.palette.textDimmed};
`;

const StyledHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledLoadingContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledErrorMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.palette.error};
`;

const SlidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(245px, 1fr));
  align-content: start;
  align-items: start;
  min-height: 60vh;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.default} 0;
  width: 100%;
`;

export const getUploadedSlides = (responseData: unknown): UploadedSlide[] => {
  if (!responseData) return [];

  try {
    const parsed: any = typeof responseData === 'string' ? JSON.parse(responseData) : responseData;
    const slides = parsed?.Slides ?? parsed?.data ?? (Array.isArray(parsed) ? parsed : []);
    return Array.isArray(slides) ? slides : [];
  } catch {
    return [];
  }
};

export const UploadNewAsset: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { uploadFiles } = useUpload();
  const [jobId, setJobId] = useState<string | null>(null);
  const { pendingUpload, rootFolderId, closeUploadNewAsset } = useUploadNewAsset();
  const [uploadRequestId, setUploadRequestId] = useState<number | null>(null);
  const [cancelUploadRequest, { isLoading: isCancelling }] = useCancelUploadRequestMutation();
  const [isClosing, setIsClosing] = useState(false);

  const hasStartedUploadRef = useRef(false);

  useEffect(() => {
    if (!pendingUpload) return;
    if (hasStartedUploadRef.current) return;
    hasStartedUploadRef.current = true;

    uploadFiles({
      file: pendingUpload.file,
      folderId: pendingUpload.targetFolderId,
      isInlineProgress: true,
    }).then((result: any) => {
      if (result?.notificationItemId) setJobId(result.notificationItemId);
      if (result?.uploadRequestId) setUploadRequestId(result.uploadRequestId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUpload]);

  const uploadItems = useSelector((state: RootState) => getLongPullUIState(state)?.['global'] || []);
  const uploadStatus = useMemo(() => uploadItems.find((item: any) => item.id === jobId), [uploadItems, jobId]);

  const apiStatus = uploadStatus?.apiStatus?.toLowerCase?.() || '';
  const isCompleted = uploadStatus?.isCompleted || ['success', 'failed', 'timeout'].includes(apiStatus);
  const hasError = uploadStatus?.type === 'error' || apiStatus === 'failed' || apiStatus === 'timeout';
  const errorMessage = hasError ? uploadStatus?.message || 'Upload failed. Please try again.' : null;

  const slides = useMemo(() => getUploadedSlides(uploadStatus?.responseData), [uploadStatus?.responseData]);

  const handleCancel = async () => {
    if (!uploadRequestId) {
      closeUploadNewAsset();
      return;
    }

    try {
      // no assetVersionId: this is a brand-new asset with no prior version, so the server
      // soft-deletes the asset itself rather than reverting to a previous version.
      const cancelResult = await cancelUploadRequest({ uploadRequestId });

      if ('error' in cancelResult && (cancelResult.error as { status?: number })?.status !== 404) {
        const message = 'Failed to cancel this upload.';
        captureError(cancelResult.error, { message, component: 'UploadNewAsset' });
        dispatch(addNotification({ type: 'error', message }));
        return;
      }

      setIsClosing(true);
      await dispatch(foldersService.endpoints.getFolderAssets.initiate({ parentId: pendingUpload!.targetFolderId }))
        .unwrap()
        .catch(() => {});
    } finally {
      closeUploadNewAsset();
    }
  };

  const { data: folderPath } = useGetFolderPathQuery(
    pendingUpload ? { folderId: pendingUpload.targetFolderId } : skipToken,
  );

  const rootIndex = folderPath?.findIndex((folder) => String(folder.id) === String(rootFolderId)) ?? -1;
  const relativeFolderPath = rootIndex !== -1 ? folderPath!.slice(rootIndex + 1) : folderPath;
  const destinationLabel = relativeFolderPath?.map((folder) => folder.name).join(' / ');

  if (!pendingUpload) return null;

  return (
    <NgCard>
      <NgCardContent>
        <NgCardHeader hideFilters>
          <StyledHeaderLeft>
            <StyledTitle>Upload New Asset</StyledTitle>
            {errorMessage ? (
              <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
            ) : (
              <StyledSubtitle>
                {slides.length ? `${slides.length} slides detected` : 'Processing...'}
                {destinationLabel && (
                  <>
                    · {isCompleted ? 'uploaded' : 'uploading'} to <strong>{destinationLabel}</strong>
                  </>
                )}
              </StyledSubtitle>
            )}
          </StyledHeaderLeft>

          <StyledHeaderRight>
            <NgButton
              variant={buttonVariants.clear}
              disabled={!uploadRequestId && !hasError}
              isLoading={isCancelling || isClosing}
              onClick={handleCancel}
            >
              Cancel
            </NgButton>

            <NgButton
              variant={buttonVariants.dark}
              // disabled={hasError}
              isLoading={!isCompleted}
              loadingSlot={
                <StyledLoadingContent>
                  <NgBuildIcon show />
                  <span>Processing…</span>
                </StyledLoadingContent>
              }
              onClick={closeUploadNewAsset}
            >
              Close
            </NgButton>
          </StyledHeaderRight>
        </NgCardHeader>

        <SlidesGrid>
          {slides.length > 0
            ? slides.map((slide) => (
                <SlideItem
                  key={slide.slide_number}
                  slideNumber={slide.slide_number}
                  thumbnailBase64={slide.thumbnail_base64}
                  slideTitle={slide.slide_title}
                  compatibilityBadge={getSlideCompatibilityBadge(slide)}
                />
              ))
            : Array.from({ length: SKELETON_SLIDE_COUNT }).map((_, index) => (
                <SkeletonLoader
                  key={index}
                  count={1}
                  height={138}
                  width={245}
                  borderRadius={theme.radius.default}
                  single
                />
              ))}
        </SlidesGrid>
      </NgCardContent>
    </NgCard>
  );
};
